import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Globe, LogOut, ArrowLeft, UserPlus, Edit, Trash2, Send, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const countries = [
  { code: 'MX', name: 'Mexico' },
  { code: 'PH', name: 'Philippines' },
  { code: 'NG', name: 'Nigeria' }
];

const accountTypes = [
  { value: 'bank', label: 'Bank Account' },
  { value: 'mobile_money', label: 'Mobile Money' },
  { value: 'wallet', label: 'Crypto Wallet' }
];

const RecipientsPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    country: 'MX',
    account_number: '',
    account_type: 'bank',
    nickname: ''
  });

  useEffect(() => {
    loadRecipients();
  }, []);

  const loadRecipients = async () => {
    try {
      const response = await axios.get(`${API}/recipients?user_id=${user.id}`);
      setRecipients(response.data);
    } catch (error) {
      console.error('Failed to load recipients:', error);
      toast.error('Failed to load recipients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingRecipient) {
        await axios.put(`${API}/recipients/${editingRecipient.id}?user_id=${user.id}`, formData);
        toast.success('Recipient updated successfully!');
      } else {
        await axios.post(`${API}/recipients?user_id=${user.id}`, formData);
        toast.success('Recipient added successfully!');
      }
      
      setDialogOpen(false);
      setEditingRecipient(null);
      setFormData({
        name: '',
        country: 'MX',
        account_number: '',
        account_type: 'bank',
        nickname: ''
      });
      loadRecipients();
    } catch (error) {
      console.error('Failed to save recipient:', error);
      toast.error('Failed to save recipient');
    }
  };

  const handleEdit = (recipient) => {
    setEditingRecipient(recipient);
    setFormData({
      name: recipient.name,
      country: recipient.country,
      account_number: recipient.account_number,
      account_type: recipient.account_type,
      nickname: recipient.nickname || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (recipientId) => {
    if (!window.confirm('Are you sure you want to delete this recipient?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/recipients/${recipientId}?user_id=${user.id}`);
      toast.success('Recipient deleted successfully!');
      loadRecipients();
    } catch (error) {
      console.error('Failed to delete recipient:', error);
      toast.error('Failed to delete recipient');
    }
  };

  const handleSendMoney = (recipient) => {
    navigate('/transfer', { state: { recipient } });
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      {/* Navigation */}
      <nav className='bg-white border-b shadow-sm'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center h-16'>
            <div className='flex items-center space-x-4'>
              <Button variant='ghost' size='icon' onClick={() => navigate('/dashboard')} data-testid='back-to-dashboard-btn'>
                <ArrowLeft className='w-5 h-5' />
              </Button>
              <div className='flex items-center space-x-2'>
                <img 
                  src='https://customer-assets.emergentagent.com/job_smart-money-route/artifacts/6teoyzj7_Generated%20Image%20November%2018%2C%202025%20-%203_06PM.png' 
                  alt='RouteFlow AI' 
                  className='h-10 w-auto'
                />
              </div>
            </div>
            <Button variant='ghost' onClick={onLogout} data-testid='logout-btn'>
              <LogOut className='w-4 h-4 mr-2' /> Logout
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className='max-w-6xl mx-auto px-4 py-8'>
        <div className='flex justify-between items-center mb-6'>
          <div>
            <h1 className='text-3xl font-bold text-gray-900 mb-2'>Recipients</h1>
            <p className='text-gray-600'>Manage your saved recipients for quick transfers</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className='bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                onClick={() => {
                  setEditingRecipient(null);
                  setFormData({
                    name: '',
                    country: 'MX',
                    account_number: '',
                    account_type: 'bank',
                    nickname: ''
                  });
                }}
                data-testid='add-recipient-btn'
              >
                <UserPlus className='w-4 h-4 mr-2' /> Add Recipient
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingRecipient ? 'Edit Recipient' : 'Add New Recipient'}</DialogTitle>
                <DialogDescription>
                  Save recipient details for quick future transfers
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='name'>Recipient Name</Label>
                  <Input
                    id='name'
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder='John Doe'
                    required
                    data-testid='recipient-name-input'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='nickname'>Nickname (Optional)</Label>
                  <Input
                    id='nickname'
                    value={formData.nickname}
                    onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                    placeholder='Mom, Friend, Supplier...'
                    data-testid='recipient-nickname-input'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='country'>Country</Label>
                  <Select value={formData.country} onValueChange={(value) => setFormData({ ...formData, country: value })}>
                    <SelectTrigger data-testid='country-select'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='account_type'>Account Type</Label>
                  <Select value={formData.account_type} onValueChange={(value) => setFormData({ ...formData, account_type: value })}>
                    <SelectTrigger data-testid='account-type-select'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {accountTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='account_number'>Account Number</Label>
                  <Input
                    id='account_number'
                    value={formData.account_number}
                    onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                    placeholder='Account number or mobile money number'
                    required
                    data-testid='account-number-input'
                  />
                </div>

                <Button type='submit' className='w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700' data-testid='save-recipient-btn'>
                  {editingRecipient ? 'Update Recipient' : 'Add Recipient'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className='flex justify-center py-12'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600'></div>
          </div>
        ) : recipients.length === 0 ? (
          <Card className='border-0 shadow-lg' data-testid='no-recipients-card'>
            <CardContent className='flex flex-col items-center justify-center py-12 text-gray-500'>
              <Users className='w-16 h-16 mb-4 opacity-50' />
              <p className='text-lg font-semibold mb-2'>No recipients yet</p>
              <p className='mb-4 text-center'>Add recipients to send money quickly in the future</p>
              <Button 
                onClick={() => setDialogOpen(true)}
                className='bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                data-testid='empty-add-recipient-btn'
              >
                <UserPlus className='w-4 h-4 mr-2' /> Add Your First Recipient
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-6'>
            {recipients.map((recipient) => (
              <Card key={recipient.id} className='border-0 shadow-lg hover:shadow-xl smooth-transition' data-testid={`recipient-card-${recipient.id}`}>
                <CardHeader>
                  <CardTitle className='flex items-center justify-between'>
                    <span>{recipient.nickname || recipient.name}</span>
                  </CardTitle>
                  {recipient.nickname && (
                    <CardDescription>{recipient.name}</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className='space-y-2 mb-4'>
                    <div className='flex justify-between text-sm'>
                      <span className='text-gray-600'>Country:</span>
                      <span className='font-semibold'>
                        {countries.find(c => c.code === recipient.country)?.name}
                      </span>
                    </div>
                    <div className='flex justify-between text-sm'>
                      <span className='text-gray-600'>Account Type:</span>
                      <span className='font-semibold capitalize'>{recipient.account_type.replace('_', ' ')}</span>
                    </div>
                    <div className='flex justify-between text-sm'>
                      <span className='text-gray-600'>Account:</span>
                      <span className='font-mono text-xs'>{recipient.account_number}</span>
                    </div>
                  </div>
                  
                  <div className='flex space-x-2'>
                    <Button 
                      onClick={() => handleSendMoney(recipient)}
                      className='flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                      data-testid={`send-to-${recipient.id}-btn`}
                    >
                      <Send className='w-4 h-4 mr-2' /> Send
                    </Button>
                    <Button 
                      variant='outline' 
                      size='icon'
                      onClick={() => handleEdit(recipient)}
                      data-testid={`edit-${recipient.id}-btn`}
                    >
                      <Edit className='w-4 h-4' />
                    </Button>
                    <Button 
                      variant='outline' 
                      size='icon'
                      onClick={() => handleDelete(recipient.id)}
                      data-testid={`delete-${recipient.id}-btn`}
                    >
                      <Trash2 className='w-4 h-4 text-red-500' />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecipientsPage;
